import { useEffect, useRef, useState } from 'react';

type ColumnWidths = Record<string, number>;

interface UseResizableColumnsOptions {
  storageKey: string;
  minWidthPx?: number;
  maxWidthPx?: number;
}

export function useResizableColumns(columnKeys: string[], options: UseResizableColumnsOptions) {
  const { storageKey, minWidthPx = 80, maxWidthPx = 800 } = options;
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved);
    } catch {}
    return {};
  });

  const draggingRef = useRef<{
    columnKey: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnWidths));
    } catch {}
  }, [columnWidths, storageKey]);

  const handleMouseDown = (columnKey: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths[columnKey] ?? (e.currentTarget.parentElement?.getBoundingClientRect().width || 0);
    draggingRef.current = { columnKey, startX, startWidth };
    document.body.style.cursor = 'col-resize';
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      if (!draggingRef.current) return;
      const { columnKey: key, startX, startWidth } = draggingRef.current;
      const delta = ev.clientX - startX;
      let next = Math.max(minWidthPx, Math.min(maxWidthPx, Math.round(startWidth + delta)));
      setColumnWidths(prev => ({ ...prev, [key]: next }));
    };

    const onMouseUp = () => {
      draggingRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = prevUserSelect;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const getStyleForColumn = (columnKey: string): React.CSSProperties => {
    const width = columnWidths[columnKey];
    if (!width) return {};
    return { width, minWidth: width, maxWidth: width, boxSizing: 'border-box' } as React.CSSProperties;
  };

  const resetColumnWidths = () => setColumnWidths({});

  return {
    columnWidths,
    handleMouseDown,
    getStyleForColumn,
    resetColumnWidths,
  };
}


